/*
 * Typed wrappers over the goserver Advisors tab endpoints. SUPER_USER or roster
 * MANAGER only (403 otherwise).
 */

import { get_api as getApi, post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import type {
  AdvisorsList,
  AssignRequest,
  AssignResult,
  Commit,
  CommitRequest,
  EndRequest,
  EndResult,
  History,
  IgnoredList,
  Preview,
  PreviewRequest
} from './types';

const BASE = '/v2/sales_task_list/advisors';

export const TARGET_INELIGIBLE = 'TARGET_INELIGIBLE';
export const SOURCE_NOT_ACTIVE = 'SOURCE_NOT_ACTIVE';
export const ALREADY_ASSIGNED = 'ALREADY_ASSIGNED';

interface Envelope<T> {
  data?: { data?: T };
}

export interface ApiError {
  response?: {
    status?: number;
    data?: {
      reason?: string;
      message?: string;
      error_code?: string;
      // Set on a 409 ALREADY_ASSIGNED.
      current?: { cms_user_uuid?: string; advisor_name?: string; row_id?: number };
    };
  };
}

const unwrap = <T>(res: Envelope<T> | null | undefined): T | undefined => res?.data?.data;

export const normalizeList = (d: Partial<AdvisorsList> | undefined): AdvisorsList => ({
  summary: Array.isArray(d?.summary) ? d.summary : [],
  rows: Array.isArray(d?.rows) ? d.rows : [],
  next_cursor: Number(d?.next_cursor) || 0
});

// `query` is the pre-built "?a=b" string from listQuery.
export const fetchAdvisors = (query: string): Promise<AdvisorsList> =>
  getApi(getApiUrl(`${BASE}${query}`), true).then((res: Envelope<Partial<AdvisorsList>>) =>
    normalizeList(unwrap(res))
  );

export const fetchHistory = (studentUuid: string): Promise<History> =>
  getApi(getApiUrl(`${BASE}/history/${encodeURIComponent(studentUuid)}`), true).then(
    (res: Envelope<Partial<History>>) => {
      const d = unwrap(res);
      return {
        student_uuid: d?.student_uuid || studentUuid,
        rows: Array.isArray(d?.rows) ? d.rows : [],
        ignored_tags: Array.isArray(d?.ignored_tags) ? d.ignored_tags : []
      };
    }
  );

export const previewReassign = (body: PreviewRequest): Promise<Preview> =>
  postApi(getApiUrl(`${BASE}/reassign/preview`), body, true).then((res: Envelope<Preview>) => {
    const d = unwrap(res) as Preview;
    return { ...d, items: Array.isArray(d?.items) ? d.items : [] };
  });

export const commitReassign = (body: CommitRequest): Promise<Commit> =>
  postApi(getApiUrl(`${BASE}/reassign/commit`), body, true).then((res: Envelope<Commit>) => {
    const d = unwrap(res) as Commit;
    return { ...d, results: Array.isArray(d?.results) ? d.results : [] };
  });

export const endAssignment = (body: EndRequest): Promise<EndResult> =>
  postApi(getApiUrl(`${BASE}/end`), body, true).then(
    (res: Envelope<EndResult>) => unwrap(res) as EndResult
  );

export const assignAdvisor = (body: AssignRequest): Promise<AssignResult> =>
  postApi(getApiUrl(`${BASE}/assign`), body, true).then(
    (res: Envelope<AssignResult>) => unwrap(res) as AssignResult
  );

export const normalizeIgnored = (d: Partial<IgnoredList> | undefined): IgnoredList => ({
  rows: Array.isArray(d?.rows) ? d.rows : [],
  summary: {
    by_reason: d?.summary?.by_reason || {},
    by_author: Array.isArray(d?.summary?.by_author) ? d.summary.by_author : []
  },
  next_cursor: Number(d?.next_cursor) || 0
});

// `query` is the pre-built "?from=..&to=.." string from ignoredQuery.
export const fetchIgnored = (query: string): Promise<IgnoredList> =>
  getApi(getApiUrl(`${BASE}/ignored${query}`), true).then((res: Envelope<Partial<IgnoredList>>) =>
    normalizeIgnored(unwrap(res))
  );

export const apiStatus = (e: unknown): number | undefined =>
  (e as ApiError | null)?.response?.status;

export const apiErrorCode = (e: unknown): string =>
  (e as ApiError | null)?.response?.data?.error_code || '';

// Name of the advisor holding the student, from a 409 ALREADY_ASSIGNED body ('' when absent).
export const apiCurrentAdvisorName = (e: unknown): string =>
  (e as ApiError | null)?.response?.data?.current?.advisor_name || '';

export const apiErrorReason = (e: unknown, fallback: string): string => {
  const data = (e as ApiError | null)?.response?.data;
  return data?.reason || data?.message || fallback;
};
